import {
  Alert,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Typography,
} from '@mui/material';
import { Crop, CheckCircle } from '@mui/icons-material';
import { Icon } from '@iconify/react';
import type { UploadReceiptFormPanelProps } from '../../../types/uploadReceiptDialog';

const UploadReceiptFormPanel = ({
  error,
  onClearError,
  stores,
  selectedStore,
  locations,
  selectedLocation,
  locationsLoading,
  locationsResolved,
  loading,
  onStoreChange,
  onLocationChange,
  selectedFile,
  fileInputRef,
  onBrowseClick,
  onFileSelect,
  onDrop,
  onDragOver,
  editMode,
  modeSwitching,
  onUseOriginal,
  onStartCornerCrop,
  onStartPerspectiveEdit,
  onStartRotateEdit,
  onFreeCropRevert,
  rotation,
  horizontalTilt,
  verticalTilt,
  onRotationChange,
  onRotationDecrease,
  onRotationIncrease,
  onHorizontalTiltChange,
  onHorizontalTiltDecrease,
  onHorizontalTiltIncrease,
  onVerticalTiltChange,
  onVerticalTiltDecrease,
  onVerticalTiltIncrease,
}: UploadReceiptFormPanelProps) => {
  return (
    <Box
      sx={{
        width: { xs: '100%', md: '45%' },
        flex: { xs: '1 1 auto', md: '0 0 45%' },
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={onClearError}>
          {error}
        </Alert>
      )}

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Select Store</InputLabel>
        <Select value={selectedStore} label="Select Store" onChange={onStoreChange} disabled={loading}>
          {stores.map((store) => (
            <MenuItem key={store.id} value={store.id}>
              {store.name} - {store.address_line1}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {locations.length > 1 && (
        <FormControl fullWidth required sx={{ mb: 3 }}>
          <InputLabel>Select Location</InputLabel>
          <Select
            value={selectedLocation}
            label="Select Location"
            onChange={onLocationChange}
            disabled={locationsLoading}
          >
            <MenuItem value="" disabled>
              Select a location
            </MenuItem>
            {locations.map((location) => (
              <MenuItem key={location.id} value={location.id}>
                {location.location_name} ({location.location_key})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {selectedStore && !locationsLoading && !locationsResolved && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Store locations could not be loaded. Upload is disabled.
        </Alert>
      )}

      <input ref={fileInputRef} type="file" accept="image/*" onChange={onFileSelect} style={{ display: 'none' }} />

      {selectedFile ? (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            bgcolor: 'background.paper',
          }}
          onDrop={onDrop}
          onDragOver={onDragOver}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <CheckCircle sx={{ color: 'success.main', flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" color="text.primary" fontWeight={700} noWrap>
                {selectedFile.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
            </Box>
          </Box>
          <Button variant="outlined" onClick={onBrowseClick} sx={{ textTransform: 'none', flexShrink: 0 }}>
            Change
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            border: '2px dashed',
            borderColor: 'grey.300',
            borderRadius: 2,
            p: 1.5,
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: '#2C6CA00A',
            '&:hover': {
              backgroundColor: '#2C6CA00A',
            },
            minHeight: '200px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={onBrowseClick}
          onDrop={onDrop}
          onDragOver={onDragOver}
        >
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={1}>
            <Box>
              <Icon icon="ep:upload-filled" width="32" height="32" style={{ color: '#2C6CA0' }} />
              <Typography variant="body1" sx={{ fontWeight: 500, fontSize: 16, color: '#2C6CA0' }}>
                Drag & Drop files here
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary">
              --- or ---
            </Typography>

            <Button
              variant="outlined"
              sx={{
                color: '#2C6CA0',
                borderColor: '#2C6CA0',
                borderRadius: '8px',
                textTransform: 'none',
                backgroundColor: '#ffffff',
              }}
            >
              Browse File
            </Button>
          </Box>
        </Box>
      )}

      {selectedFile && (
        <Box sx={{ mt: 3 }}>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 1.5,
              bgcolor: 'background.paper',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                  {editMode === 'none' ? 'Original image selected' : 'Editing image'}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {editMode === 'none' ? 'No edits will be applied.' : 'Upload will use the selected edit.'}
                </Typography>
              </Box>
              {editMode === 'none' ? (
                <Button
                  variant="outlined"
                  onClick={onStartCornerCrop}
                  disabled={modeSwitching}
                  startIcon={<Crop />}
                  sx={{ textTransform: 'none', flexShrink: 0 }}
                >
                  Edit image
                </Button>
              ) : (
                <Button
                  variant="text"
                  onClick={onUseOriginal}
                  disabled={modeSwitching}
                  sx={{ textTransform: 'none', flexShrink: 0 }}
                >
                  Use original
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {selectedFile && editMode !== 'none' && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant={editMode === 'free' ? 'contained' : 'outlined'}
              onClick={onStartCornerCrop}
              disabled={modeSwitching}
              startIcon={<Crop />}
              sx={{ textTransform: 'none' }}
            >
              Crop
            </Button>
            <Button
              variant={editMode === 'perspective' ? 'contained' : 'outlined'}
              onClick={onStartPerspectiveEdit}
              disabled={modeSwitching}
              startIcon={<Icon icon="mdi:perspective-less" width="20" height="20" />}
              sx={{ textTransform: 'none' }}
            >
              Straighten
            </Button>
            <Button
              variant={editMode === 'tilt' ? 'contained' : 'outlined'}
              onClick={onStartRotateEdit}
              disabled={modeSwitching}
              startIcon={<Icon icon="mdi:rotate-right" width="20" height="20" />}
              sx={{ textTransform: 'none' }}
            >
              Rotate
            </Button>
          </Box>
        </Box>
      )}

      {selectedFile && editMode === 'free' && (
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={onFreeCropRevert}
            disabled={modeSwitching}
            size="medium"
            color="secondary"
            sx={{ textTransform: 'none' }}
          >
            Reset crop
          </Button>
        </Box>
      )}

      {selectedFile && editMode === 'perspective' && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ mb: 1 }}>
            Straighten receipt
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Rotation: {rotation}°
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <IconButton size="small" onClick={onRotationDecrease} disabled={rotation <= -180}>
              <Icon icon="lsicon:anticlockwise-90-filled" width="24" height="24" style={{ color: '#2C6CA0' }} />
            </IconButton>
            <Slider
              size="small"
              min={-180}
              max={180}
              step={1}
              value={rotation}
              onChange={onRotationChange}
              sx={{ flex: 1 }}
            />
            <IconButton size="small" onClick={onRotationIncrease} disabled={rotation >= 180}>
              <Icon icon="lsicon:clockwise-90-filled" width="24" height="24" style={{ color: '#2C6CA0' }} />
            </IconButton>
          </Box>

          <Typography variant="caption" color="text.secondary">
            Horizontal tilt: {horizontalTilt.toFixed(2)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <IconButton size="small" onClick={onHorizontalTiltDecrease} disabled={horizontalTilt <= -1}>
              -
            </IconButton>
            <Slider
              size="small"
              min={-1}
              max={1}
              step={0.01}
              value={horizontalTilt}
              onChange={onHorizontalTiltChange}
              sx={{ flex: 1 }}
            />
            <IconButton size="small" onClick={onHorizontalTiltIncrease} disabled={horizontalTilt >= 1}>
              +
            </IconButton>
          </Box>

          <Typography variant="caption" color="text.secondary">
            Vertical tilt: {verticalTilt.toFixed(2)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={onVerticalTiltDecrease} disabled={verticalTilt <= -1}>
              -
            </IconButton>
            <Slider
              size="small"
              min={-1}
              max={1}
              step={0.01}
              value={verticalTilt}
              onChange={onVerticalTiltChange}
              sx={{ flex: 1 }}
            />
            <IconButton size="small" onClick={onVerticalTiltIncrease} disabled={verticalTilt >= 1}>
              +
            </IconButton>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default UploadReceiptFormPanel;
